import { useMemo, useState } from 'react'
import { Typography } from '../common/typography'
import Tabs from '../stories/Tabs/Tabs'
import classes from './Learning.module.css'
import { AspectRatio, Image, Table, TextInput } from '@mantine/core'
import { IconZoom } from '@tabler/icons-react'
import { useCompetitionId } from '../common/hooks'
import {
  usePlayersLearningRanks,
  useTeamsLearningRanks,
  type Team,
  type Player,
} from './api'
import { getRankSuffix } from '../common/helpers'
import { useNavigate } from 'react-router'
import clsx from 'clsx'
import { useMediaQuery } from '@mantine/hooks'
import NearlyThere from '../assets/nearly-there.png'
import Platinum from '../assets/platinum.png'
import Gold from '../assets/gold.png'
import Silver from '../assets/silver.png'
import Bronze from '../assets/bronze.png'

const LevelIcon: Record<string, string> = {
  Platinum,
  Gold,
  Silver,
  Bronze,
  'Nearly there': NearlyThere,
}

export default function XPRank() {
  const tabs = [
    {
      value: 'teams',
      label: 'Teams',
    },
    {
      value: 'players',
      label: 'Players',
    },
  ]
  const [activeTab, setActiveTab] = useState<string>('teams')
  const [searchValue, setSearchValue] = useState<string>('')
  const competition_id = useCompetitionId()
  const { data: teamsLearningRanks } = useTeamsLearningRanks({ competition_id })
  const { data: playersLearningRanks } = usePlayersLearningRanks({
    competition_id,
  })
  const isMobile = useMediaQuery('(max-width: 800px)')
  const navigate = useNavigate()

  const filteredData = useMemo(() => {
    const data =
      activeTab === 'teams'
        ? { type: 'team' as const, data: teamsLearningRanks }
        : { type: 'player' as const, data: playersLearningRanks }

    if (!searchValue.trim()) return data

    const search = searchValue.toLowerCase()

    if (data.type === 'team') {
      return {
        type: 'team' as const,
        data: data.data.filter((rank) =>
          rank.team_name.toLowerCase().includes(search)
        ),
      }
    } else {
      return {
        type: 'player' as const,
        data: data.data.filter((rank) =>
          rank.player_name.toLowerCase().includes(search)
        ),
      }
    }
  }, [activeTab, teamsLearningRanks, playersLearningRanks, searchValue])

  const handleRowClick = (id: number) => {
    if (filteredData.type === 'team') {
      navigate(`/competitions/${competition_id}/teams/${id}/overview`)
    } else {
      navigate(`/competitions/${competition_id}/players/${id}`)
    }
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    setSearchValue('') // Clear search on tab switch
  }

  const rows = filteredData.data.map((item) => {
    const isTeam = filteredData.type === 'team'
    const typedItem = isTeam ? (item as Team) : (item as Player)
    const id = isTeam ? typedItem.team_id : (typedItem as Player).player_id
    const name = isTeam
      ? typedItem.team_name
      : (typedItem as Player).player_name
    const rank = typedItem.rank
    const src = LevelIcon[typedItem.xp_level]

    return (
      <Table.Tr key={id} onClick={() => handleRowClick(id)}>
        <Table.Td>
          <Typography lh="150" ta="center">
            {rank}
            <sup>{getRankSuffix(rank)}</sup>
          </Typography>
        </Table.Td>

        <Table.Td>
          <div className={classes.nameColumn}>
            <AspectRatio ratio={1 / 1}>
              <Image
                src={typedItem.team_logo.thumbnail}
                alt={typedItem.team_name}
                className={clsx(classes.logo, {
                  [classes.logoMobile]: isMobile,
                })}
                fit="scale-down"
              />
            </AspectRatio>
            {!isTeam && (
              <AspectRatio ratio={1 / 1}>
                <Image
                  src={(typedItem as Player).player_photo.thumbnail}
                  alt={(typedItem as Player).player_name}
                  className={clsx(classes.logo, classes.playerPhoto, {
                    [classes.logoMobile]: isMobile,
                  })}
                  fit="scale-down"
                />
              </AspectRatio>
            )}
            <Typography lh="150">{name}</Typography>
          </div>
        </Table.Td>
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="150" ta="center">
            {typedItem.xp_rating.toFixed(2)}
          </Typography>
        </Table.Td>
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="150" ta="center">
            {Math.round(typedItem.xp_points)}
          </Typography>
        </Table.Td>
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="150" ta="center">
            {Math.round(typedItem.completed_percentage)}%
          </Typography>
        </Table.Td>
        <Table.Td>
          <div className={classes.learningXPCell}>
            <AspectRatio className={classes.levelIcon}>
              <Image src={src} fit="scale-down" />
            </AspectRatio>
            <Typography lh="150">
              {typedItem.xp_level === 'Nearly there'
                ? 'Provisional'
                : typedItem.xp_level}
            </Typography>
          </div>
        </Table.Td>
      </Table.Tr>
    )
  })

  return (
    <div className={classes.xpRankContainer}>
      <div className={classes.xpRankHeader}>
        <Tabs items={tabs} value={activeTab} onChange={handleTabChange} />
        <TextInput
          placeholder="Search"
          value={searchValue}
          onChange={(event) => setSearchValue(event.currentTarget.value)}
          rightSection={<IconZoom className={classes.searchIcon} />}
          rightSectionPointerEvents="none"
          size="xs"
          classNames={{
            input: classes.searchInput,
          }}
        />
      </div>

      <div className={classes.tableContainer}>
        <Table.ScrollContainer minWidth={300}>
          <Table
            classNames={{
              table: clsx(
                classes.table,
                isMobile && classes.stickyXpRankColumns
              ),
              tr: classes.tr,
              th: classes.th,
              td: classes.td,
            }}
            verticalSpacing="xs"
            withRowBorders={false}
          >
            <Table.Thead>
              <Table.Tr className={classes.headerRow}>
                <Table.Th>
                  <Typography
                    fw={600}
                    fc={{ light: 'gray-9', dark: 'gray-9' }}
                    lh="150"
                    ta="center"
                  >
                    #
                  </Typography>
                </Table.Th>
                <Table.Th>
                  <Typography
                    fw={600}
                    fc={{ light: 'gray-9', dark: 'gray-9' }}
                    lh="150"
                    ta="left"
                  >
                    {activeTab === 'teams' ? 'Team' : 'Player'}
                  </Typography>
                </Table.Th>
                <Table.Th>
                  <Typography
                    fw={600}
                    fc={{ light: 'gray-9', dark: 'gray-9' }}
                    lh="150"
                    ta="center"
                  >
                    XP Rating
                  </Typography>
                </Table.Th>
                <Table.Th>
                  <Typography
                    fw={600}
                    fc={{ light: 'gray-9', dark: 'gray-9' }}
                    lh="150"
                    ta="center"
                  >
                    XP Points
                  </Typography>
                </Table.Th>
                <Table.Th>
                  <Typography
                    fw={600}
                    fc={{ light: 'gray-9', dark: 'gray-9' }}
                    lh="150"
                    ta="center"
                  >
                    Completed
                  </Typography>
                </Table.Th>
                <Table.Th>
                  <Typography
                    fw={600}
                    fc={{ light: 'gray-9', dark: 'gray-9' }}
                    lh="150"
                    ta="center"
                  >
                    XP Level
                  </Typography>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredData.data.length > 0 ? (
                rows
              ) : searchValue ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Typography ta="center">
                      No results found matching "{searchValue}"
                    </Typography>
                  </Table.Td>
                </Table.Tr>
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Typography ta="center">No data available</Typography>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>
    </div>
  )
}
